import { css } from '@compiled/react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Banner from '@atlaskit/banner';
import { cssMap } from '@atlaskit/css';
import type { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import type { EditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { useExtensionOptions } from '@confluence/fabric-extension-handlers/entry-points/useExtensionOptions.ts';
import { createFabricMentionResource } from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import { fg } from '@confluence/feature-gating';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { withEditorActions } from './withEditorActions.tsx';

const useFocusedMentionIdQueryParam = createSingleQueryParamHook('focusedMentionId');

export type NBMEditorProps = {
	editorApi: any;
	preset: EditorPresetBuilder<any, any>;
	featureFlags: Record<string, boolean | string>;
	document: JSONDocNode;
	mountPoint: HTMLElement;
	boundariesElement: HTMLElement;
	setInnerEditorView: (editorView: EditorView) => void;
	handleContentChanges: (updatedDoc: Node) => void;
	annotationProvider?: any;
	contentMode?: 'standard' | 'compact' | undefined;
} & {
	editorActions: EditorActions;
};

const reduceNestedBodiedMacrosPadding = css({
	// Reduce the padding between the LCM and bodied macros nested inside
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .appearance-full-page': {
		padding: `0 ${token('space.400')}`,
	},
});

const updateEditorScrollParentOverflow = css({
	// Update the scroll parent of the nested editor so we don't have a scrollbar for legacy macro editor
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .fabric-editor-popup-scroll-parent': {
		overflowY: 'auto',
	},
});

const reduceEditorWhitespace = css({
	// Reduce the padding around the LCM editor to better align with the TinyMCE look-and-feel
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .ak-editor-content-area-region': {
		paddingTop: token('space.025'),
		paddingBottom: token('space.075'),
	},
	// Leave enough space for the extension labels
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-testid="new-lozenge-container"]': {
		marginTop: token('space.400'),
		// We need to add a child selector to make sure the overrride works with specificity
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.bodied': {
			marginTop: token('space.400'),
		},
	},
});

const transparentExtensionContent = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .extension-content': {
		// Remove the default background color of the extension container
		backgroundColor: 'transparent',
	},
});

const collapsedEmptyLists = css({
	// Hide the bullets for deeply nested lists without sibling content so that they instead appear as if they are indented (a common formatting strategy in legacy editor content)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'& li:has(> ul:only-child), & li:has(> ol:only-child)': {
		listStyleType: 'none',
		paddingLeft: 0,
	},
});

const styles = cssMap({
	collabWarningWrapper: {
		border: `${token('border.width')} solid ${token('color.border.warning')}`,
		borderBottomLeftRadius: token('radius.small'),
		borderBottomRightRadius: token('radius.small'),
	},
});

const i18n = defineMessages({
	collabWarning: {
		id: 'experiment-legacy-content-macro.collab-warning',
		defaultMessage: 'Collaborative features are not supported in legacy content.',
		description:
			'Warning given when two or more users are editing a document with a legacy content macro inside it',
	},
	collabWarningInfo: {
		id: 'experiment-legacy-content-macro.collab-warning',
		defaultMessage:
			'Editing or publishing this content while others are editing may result in lost changes.',
		description:
			'Information given when two or more users are editing a document with a legacy content macro inside it. This info is behind a tooltip',
	},
});

export const NBMEditorComponent: React.FC<NBMEditorProps> = ({
	editorApi,
	preset,
	featureFlags,
	document,
	mountPoint,
	boundariesElement,
	setInnerEditorView,
	handleContentChanges,
	annotationProvider,
	editorActions,
	contentMode,
}) => {
	const { cloudId, userId } = useSessionData();
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();

	const [{ collabEditProvider }] = useNativeCollabState();
	const [hasCollabUsers, setHasCollabUsers] = useState(false);
	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleUpdatePresence = () => {
			const participants = collabEditProvider.getParticipants();
			setHasCollabUsers(participants && participants.length > 1);
		};

		collabEditProvider.on('presence', handleUpdatePresence);
		handleUpdatePresence();

		return () => {
			collabEditProvider.off('presence', handleUpdatePresence);
		};
	}, [collabEditProvider]);

	const intl = useIntl();

	// mention configs
	const { openCoreInvites, userRole } = useContext(CoreInvitesContext);
	const focusedMentionId = useFocusedMentionIdQueryParam();
	const mentionProvider = useMemo(() => {
		return createFabricMentionResource(
			cloudId,
			spaceId,
			userId,
			undefined,
			openCoreInvites,
			userRole,
			focusedMentionId,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cloudId, spaceId, userId, openCoreInvites, userRole]);

	const { extensionOptions } = useExtensionOptions({
		contentId: contentId || '',
		contentType: contentType || 'page',
		editorActions,
		mentionProvider,
		spaceKey: spaceKey || '',
		spaceId,
		allowCreateDatabaseExtension: true,
		allowDatabaseQuickInsert: true,
		allowCreateWhiteboardExtension: true,
		editorAPI: editorApi ?? undefined,
		allowLegacyMacros: true,
	});

	return (
		<div
			data-testid="legacy-content-macro"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className="extension-editable-area"
			css={[
				updateEditorScrollParentOverflow,
				fg('platform_editor_lcm_transparent_extension_content') && transparentExtensionContent,
				reduceNestedBodiedMacrosPadding,
				reduceEditorWhitespace,
				fg('platform_editor_lce_collapse_bullets') && collapsedEmptyLists,
				hasCollabUsers && styles.collabWarningWrapper,
			]}
		>
			{hasCollabUsers && (
				<div>
					<Banner
						appearance="warning"
						icon={<StatusWarningIcon label={intl.formatMessage(i18n.collabWarningInfo)} />}
					>
						{intl.formatMessage(i18n.collabWarning)}
					</Banner>
				</div>
			)}
			<ComposableEditor
				annotationProviders={annotationProvider}
				appearance={
					expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
						? 'max'
						: 'full-width'
				}
				preset={preset}
				popupsBoundariesElement={boundariesElement || undefined}
				popupsMountPoint={mountPoint || undefined}
				defaultValue={document}
				mentionProvider={mentionProvider}
				skipValidation
				onChange={(value) => handleContentChanges(value.state.doc)}
				onEditorReady={(editorActions) => {
					const editorView = editorActions._privateGetEditorView();

					if (editorView) {
						if (setInnerEditorView) {
							setInnerEditorView(editorView);
						}
					}
				}}
				{...extensionOptions}
				featureFlags={featureFlags}
				contentMode={contentMode}
			/>
		</div>
	);
};

export const NBMEditor = withEditorActions(NBMEditorComponent);
